import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  pwn201Api,
  FN110,
  FN111,
  FN112,
  FN116,
  FN117,
  FN111A,
} from '../../../services/pwn201Api';
import { PWN201Mode } from './usePWN201Mode';

const defaultMaster = (): FN110 => ({
  fn110_01: '',
  fn110_11: null,
  fn110_12: '',
  fn110_121: '',
  fn110_13: '',
  fn110_14: '',
  fn110_15: 0,
  fn110_16: 0,
  fn110_17: 0,
  fn110_18: 0,
  fn110_19: '',
  fn110_201: '',
  fn110_202: 0,
  fn110_211: '',
  fn110_212: 0,
  fn110_22: '',
  fn110_23: 1,
  fn110_28: '',
  fn110_29: false,
  fn110_30: '',
  fn110_31: '',
  fn110_32: 0,
  fn110_33: 0,
  fn110_34: 0,
  fn110_35: 0,
  fn110_36: 0,
  fn110_37: 0,
  fn110_38: 0,
  fn110_71: '',
  fn110_72: '',
  fn110_73: '',
  fn110_74: '',
  fn110_75: '',
  fn110_80: 'N',
  fn110_801: '',
  fn110_8011: '',
  fn110_802: '',
  fn110_8021: '',
  fn110_803: false,
  fn110_81: 'N',
  fn110_82: '',
  fn110_83: '',
  fn110_84: '',
  fn110_841: '',
  fn110_842: null,
  fn110_85: '',
  fn110_851: '',
  fn110_852: null,
  fn110_131: '',
  fn110_132: '',
  fn110_134: '',
  fn110_221: '',
  fn110_90: 0,
  fn110_91: '',
  fn110_92: null,
  fn110_93: '',
  fn110_94: '',
});

export function usePWN201Record(setMode: (m: PWN201Mode) => void) {
  const [record, setRecord] = useState<FN110 | null>(null);
  const [formData, setFormData] = useState<FN110>(defaultMaster());
  const [shipDetails, setShipDetails] = useState<FN111[]>([]);
  const [handleDetails, setHandleDetails] = useState<FN112[]>([]);
  const [messages, setMessages] = useState<FN116[]>([]);
  const [attachments, setAttachments] = useState<FN117[]>([]);
  const [offsetDetails, setOffsetDetails] = useState<FN111A[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwn201Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setShipDetails(data.ship_details ?? []);
      setHandleDetails(data.handle_details ?? []);
      setMessages(data.messages ?? []);
      setAttachments(data.attachments ?? []);
      setOffsetDetails(data.offset_details ?? []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData(defaultMaster());
    setShipDetails([]);
    setHandleDetails([]);
    setMessages([]);
    setAttachments([]);
    setOffsetDetails([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setShipDetails(record.ship_details ?? []);
      setHandleDetails(record.handle_details ?? []);
      setMessages(record.messages ?? []);
      setAttachments(record.attachments ?? []);
      setOffsetDetails(record.offset_details ?? []);
    }
  }, [record]);

  const getField = useCallback(
    (field: keyof FN110) => formData[field],
    [formData]
  );

  const setField = useCallback(
    (field: keyof FN110, value: unknown) =>
      setFormData((prev) => ({ ...prev, [field]: value })),
    []
  );

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      const req = {
        master: formData,
        ship_details: shipDetails,
        handle_details: handleDetails,
        messages,
        attachments,
        offset_details: offsetDetails,
      };
      let saved: FN110;
      if (!record) {
        saved = await pwn201Api.create(req);
      } else {
        saved = await pwn201Api.update(record.fn110_01, req);
      }
      setRecord(saved);
      setFormData({ ...saved });
      setShipDetails(saved.ship_details ?? []);
      setHandleDetails(saved.handle_details ?? []);
      setMessages(saved.messages ?? []);
      setAttachments(saved.attachments ?? []);
      setOffsetDetails(saved.offset_details ?? []);
      setMode('browse');
      message.success('存檔成功');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: { message: string }[] } } };
      const errs = axiosErr?.response?.data?.errors;
      if (errs && errs.length > 0) {
        message.error(errs.map((e) => e.message).join('\n'));
      } else {
        message.error('存檔失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, shipDetails, handleDetails, messages, attachments, offsetDetails, record, setMode]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    setLoading(true);
    try {
      await pwn201Api.delete(record.fn110_01);
      setRecord(null);
      setFormData(defaultMaster());
      setShipDetails([]);
      setHandleDetails([]);
      setMessages([]);
      setAttachments([]);
      setOffsetDetails([]);
      setMode('browse');
      message.success('刪除成功');
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record, setMode]);

  // Detail row management helpers
  const addShipRow = useCallback(() =>
    setShipDetails((prev) => [...prev, { fn111_01: '', fn111_02: prev.length + 1, fn111_11: '', fn111_12: 0, fn111_13: '', fn111_14: 0, fn111_15: '', fn111_16: 0, fn111_17: '', fn111_18: '', fn111_181: '', fn111_182: '', fn111_19: 0, fn111_20: null, fn111_21: null, fn111_22: null, fn111_23: 0, fn111_24: 0, fn111_25: 0, fn111_26: '', fn111_27: '', fn111_90: 0 }]),
  []);

  const deleteShipRow = useCallback((idx: number) =>
    setShipDetails((prev) => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn111_02: i + 1 }))),
  []);

  const addHandleRow = useCallback(() =>
    setHandleDetails((prev) => [...prev, { fn112_01: '', fn112_02: prev.length + 1, fn112_11: '', fn112_11a: '', fn112_90: 0, fn112_12: '', fn112_13: 1, fn112_14: 0 }]),
  []);

  const deleteHandleRow = useCallback((idx: number) =>
    setHandleDetails((prev) => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn112_02: i + 1 }))),
  []);

  const addMessageRow = useCallback(() =>
    setMessages((prev) => [...prev, { fn116_01: '', fn116_02: prev.length + 1, fn116_11: '', fn116_11a: '', fn116_12: null, fn116_13: '', fn116_14: null, fn116_15: '', fn116_16: 0, fn116_90: 0, fn116_03: '' }]),
  []);

  const deleteMessageRow = useCallback((idx: number) =>
    setMessages((prev) => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn116_02: i + 1 }))),
  []);

  const addAttachmentRow = useCallback(() =>
    setAttachments((prev) => [...prev, { fn117_01: '', fn117_02: prev.length + 1, fn117_11: '', fn117_21: 1, fn117_91: '', fn117_92: null, fn117_93: '' }]),
  []);

  const deleteAttachmentRow = useCallback((idx: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn117_02: i + 1 }))),
  []);

  const addOffsetRow = useCallback(() =>
    setOffsetDetails((prev) => [...prev, { fn111a_01: '', fn111a_02: prev.length + 1, fn111a_11: '', fn111a_12: 0, fn111a_13: '', fn111a_12a: 0, fn111a_22: 0, fn111a_91: '', fn111a_92: null, fn111a_93: '' }]),
  []);

  const deleteOffsetRow = useCallback((idx: number) =>
    setOffsetDetails((prev) => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn111a_02: i + 1 }))),
  []);

  return {
    record,
    formData,
    shipDetails,
    handleDetails,
    messages,
    attachments,
    offsetDetails,
    loading,
    loadRecord,
    resetForAdd,
    revertToRecord,
    getField,
    setField,
    handleSave,
    handleDelete,
    setShipDetails,
    setHandleDetails,
    setMessages,
    setAttachments,
    setOffsetDetails,
    addShipRow,
    deleteShipRow,
    addHandleRow,
    deleteHandleRow,
    addMessageRow,
    deleteMessageRow,
    addAttachmentRow,
    deleteAttachmentRow,
    addOffsetRow,
    deleteOffsetRow,
  };
}
